import { useEffect, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useQuizBankFilterState, useQuizBankSearchFallback, useQuizBankSearchPages } from '../hooks';
import { type QuizBankPlace } from '../tracking/events.quiz-bank';
import { type QuizBankProblem } from '../types';
import { toQuizBankFacetViewModel, toQuizBankInitialGradeId, toQuizBankProblems } from '../utils';
import { QuizBankView } from './QuizBankView';
export interface QuizBankViewContainerProps {
  /** 7자리 카테고리 트리 id. 초등 수학이 아니면 서버가 400 을 주므로 호출부가 미리 거른다 */
  categoryTreeId: string;
  /**
   * 이 화면이 열린 표면. 같은 컴포넌트가 신규 생성 모달과 편집 화면 재진입 양쪽에서
   * 열리므로, 트래킹이 두 퍼널을 가르려면 호출하는 쪽이 자기 자리를 말해야 한다.
   */
  place: QuizBankPlace;
  addedProblemIds?: string[];
  maxSelectableCount?: number;
  onAdd: (problems: QuizBankProblem[]) => void;
}

/** 조회를 담당하는 컨테이너. 스토리는 `QuizBankView` 를 직접 마운트한다. */
export const QuizBankViewContainer = ({
  categoryTreeId,
  place,
  addedProblemIds,
  maxSelectableCount,
  onAdd
}: QuizBankViewContainerProps) => {
  const {
    t
  } = useTranslation(['build']);

  // 앞단에서 고른 학년을 미리 적용해 목록과 radio 가 같은 상태로 시작한다.
  const {
    filter,
    changeFilter
  } = useQuizBankFilterState({
    gradeId: toQuizBankInitialGradeId(categoryTreeId)
  });
  const {
    items,
    facets,
    matchedUnits,
    matchedStandards,
    totalCount,
    status,
    hasNextPage,
    isFetchingNextPage,
    isNextPageError,
    fetchNextPage,
    refetch
  } = useQuizBankSearchPages({
    categoryTreeId,
    filter
  });
  const problems = useMemo(() => toQuizBankProblems(items), [items]);
  // 단원명 하이라이트는 문항이 아니라 단원 단위로 오므로 카드가 unitId 로 찾는다.
  const unitHitTermsById = useMemo(() => new Map((matchedUnits ?? []).map(unit => [unit.unitId, unit.hitTerms] as const)), [matchedUnits]);
  // 코드 검색이면 그 코드를 단 카드의 성취기준 배지를 칠한다 — 배지는 코드 원문 그대로라 집합이면 된다.
  const matchedStandardCodes = useMemo(() => new Set(matchedStandards ?? []), [matchedStandards]);

  // 학년은 언제나 하나가 잡혀 있어야 한다. 전학년 진입이면 문항이 있는 첫 학년을 고른다.
  useEffect(() => {
    if (filter.gradeId || facets == null || facets.grades.length === 0) return;
    const seed = facets.grades.find(grade => grade.count > 0)?.code ?? facets.grades[0]?.code;
    if (seed) changeFilter({
      ...filter,
      gradeId: seed
    });
  }, [facets, filter, changeFilter]);

  // 검색이 현재 필터로 0건이면 건수가 남은 축을 풀어 다시 조회한다. 사용자 변경은 이 핸들러로 가야 완화와 갈린다.
  const {
    onUserFilterChange: handleFilterChange,
    fallback
  } = useQuizBankSearchFallback({
    filter,
    changeFilter,
    facets,
    totalCount,
    status
  });
  const {
    gradeOptions,
    unitOptions,
    typeOptions
  } = useMemo(() => toQuizBankFacetViewModel(t, facets, filter), [t, facets, filter]);
  return <QuizBankView place={place} categoryTreeId={categoryTreeId} problems={problems} unitHitTermsById={unitHitTermsById} matchedStandardCodes={matchedStandardCodes} gradeOptions={gradeOptions} unitOptions={unitOptions} typeOptions={typeOptions} totalCount={totalCount} filter={filter} onFilterChange={handleFilterChange} fallback={fallback} status={status} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} isNextPageError={isNextPageError} fetchNextPage={fetchNextPage} addedProblemIds={addedProblemIds} maxSelectableCount={maxSelectableCount} onAdd={onAdd} onRetry={refetch} data-sentry-element="QuizBankView" data-sentry-component="QuizBankViewContainer" data-sentry-source-file="QuizBankViewContainer.tsx" />;
};